'use server'

import { Department, DepartmentAiResumeMode } from '../common/interfaces/department.interface'
import { apiRequest, ApiResponse } from '../request'

export async function getDepartments(filters?: { organizationId?: number }) {
    return await apiRequest<Department>('departments', { params: filters })
}

export async function getDepartment(id: number) {
    return await apiRequest<Department>('departments/' + id, {})
}

export async function getDepartmentsByOrganization(
    organizationId: number,
    partnerIdOrParams?:
        | number
        | null
        | { page?: number; limit?: number; search?: string },
): Promise<ApiResponse<Department[]>> {
    const params: any =
        typeof partnerIdOrParams === 'number'
            ? { partnerId: partnerIdOrParams }
            : partnerIdOrParams ?? {}
    return apiRequest<Department[]>(`departments/organization/${organizationId}`, { params })
}

export async function createDepartment(data: {
    name: string
    description?: string
    workCalendar?: string
    whatsappNumber?: string
    whatsappProvider?: 'WHATSAPP_API' | 'WHATSAPP_WEB'
    organizationId: number
    partnerId?: number
    isDefaultForNotifications?: boolean
    createsSalesOpportunities?: boolean
    aiResumeMode?: DepartmentAiResumeMode
    routeToDepartmentId?: number | null
}): Promise<ApiResponse<Department>> {
    return apiRequest<Department>('departments', { data }, 'post')
}

export async function updateDepartment(
    id: number,
    data: {
        name?: string
        description?: string
        workCalendar?: string
        whatsappNumber?: string
        whatsappProvider?: 'WHATSAPP_API' | 'WHATSAPP_WEB'
        isDefaultForNotifications?: boolean
        createsSalesOpportunities?: boolean
        aiResumeMode?: DepartmentAiResumeMode
        flowSendLimitPerHour?: number | null
        flowSendLimitPerDay?: number | null
        routeToDepartmentId?: number | null
    }
): Promise<ApiResponse<Department>> {
    return apiRequest<Department>(`departments/${id}`, { data }, 'patch')
}

export async function deleteDepartment(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`departments/${id}`, {}, 'delete')
}

export interface DepartmentMember {
    id: number
    userId: number
    createdAt: string
    user: {
        id: number
        name: string
        email: string | null
        userType: string
        active: boolean
    }
}

/**
 * Membros do setor — quem enxerga os atendimentos dele. Lista vazia significa
 * que ninguém vê: o setor nasce fechado.
 */
export async function getDepartmentMembers(
    departmentId: number
): Promise<ApiResponse<DepartmentMember[]>> {
    return apiRequest<DepartmentMember[]>(`departments/${departmentId}/members`, {})
}

/** Substitui a composição inteira do setor. Exige permissão de edição. */
export async function setDepartmentMembers(
    departmentId: number,
    userIds: number[]
): Promise<ApiResponse<DepartmentMember[]>> {
    return apiRequest<DepartmentMember[]>(
        `departments/${departmentId}/members`,
        { data: { userIds } },
        'put'
    )
}


/** Contato que a IA não responde neste setor. A conversa acontece normalmente. */
export interface IgnoredContact {
    id: number
    phone: string
    phoneFormatted: string
    clientId: number | null
    name: string | null
    reason: string | null
    createdAt: string
    createdBy: string | null
}

/** Contato que já escreveu para o setor, com a marca de quem já está ignorado. */
export interface DepartmentContact {
    chatId: number
    phone: string
    phoneFormatted: string
    name: string | null
    clientId: number | null
    lastMessageAt: string
    ignored: boolean
}

export interface PaginatedContacts<T> {
    items: T[]
    total: number
    page: number
    limit: number
    totalPages: number
}

export async function getIgnoredContacts(
    departmentId: number,
    params?: { page?: number; limit?: number; search?: string }
): Promise<ApiResponse<PaginatedContacts<IgnoredContact>>> {
    return apiRequest<PaginatedContacts<IgnoredContact>>(
        `departments/${departmentId}/ignored-contacts`,
        { params }
    )
}

export async function getDepartmentContacts(
    departmentId: number,
    params?: { page?: number; limit?: number; search?: string }
): Promise<ApiResponse<PaginatedContacts<DepartmentContact>>> {
    return apiRequest<PaginatedContacts<DepartmentContact>>(
        `departments/${departmentId}/contacts`,
        { params }
    )
}

export async function addIgnoredContact(
    departmentId: number,
    data: {
        phone: string
        clientId?: number | null
        contactName?: string | null
        reason?: string | null
    }
): Promise<ApiResponse<IgnoredContact>> {
    return apiRequest<IgnoredContact>(
        `departments/${departmentId}/ignored-contacts`,
        { data },
        'post'
    )
}

export async function removeIgnoredContact(
    departmentId: number,
    contactId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `departments/${departmentId}/ignored-contacts/${contactId}`,
        {},
        'delete'
    )
}
